import { z } from 'zod';
// Define schema for client-side env vars
const clientSchema = z.object({
    REACT_APP_API_URL: z.string().url().optional(),
    REACT_APP_PUBLIC_STRIPE_PUBLISHABLE_KEY: z.string().min(1),
    NODE_ENV: z.enum(['development', 'production', 'test']),
    REACT_APP_SUPABASE_URL: z.string().url(),
    REACT_APP_SUPABASE_ANON_KEY: z.string().min(1),
    REACT_APP_SOCKET_URL: z.string().url().optional(),
    REACT_APP_SOCKET_SERVER: z.string().url().optional(),
    REACT_APP_TURN_SERVERS: z.string().optional(),
    REACT_APP_API_SERVER: z.string().url().optional(),
    REACT_APP_TUTOR_API_URL: z.string().url().optional(),
    REACT_APP_MEETINGS_API_URL: z.string().url().optional(),
    REACT_APP_USE_MOCK_DATA: z.string().optional(),
    // Sensitive API keys removed from client-side
    // Cloudflare R2 Configuration
    // Sensitive variables removed from client-side schema
    REACT_APP_QUIZ_PROJECT_URL: z.string().url().optional().or(z.string().length(0)),
    REACT_APP_REFERENCES_R2_WORKER_URL: z.string().url().optional().or(z.string().length(0)),
    REACT_APP_IMAGE_CDN_URL: z.string().url().optional(),
    // Support for REACT_PUBLIC_ prefix (Vite convention)
    REACT_PUBLIC_API_URL: z.string().url().optional(),
});
// Debug environment variables
// Log available environment variables in development (for debugging)
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
    const availableVars = Object.keys(process.env)
        .filter(key => key.startsWith('REACT_APP_'))
        .map(key => ({ key, set: !!process.env[key] }));
    if (availableVars.length > 0) {
        console.debug('🔍 Available REACT_APP_* environment variables:', availableVars);
    }
    else {
        console.warn('⚠️  No REACT_APP_* environment variables found in process.env');
    }
}
// Read exact values from process.env (injected at build time by webpack)
// NO FALLBACKS - values must be set in root .env (local) or system env (production)
let clientEnv = {
    REACT_APP_API_URL: process.env.REACT_APP_API_URL,
    REACT_APP_PUBLIC_STRIPE_PUBLISHABLE_KEY: process.env.REACT_APP_PUBLIC_STRIPE_PUBLISHABLE_KEY,
    NODE_ENV: process.env.NODE_ENV,
    REACT_APP_SUPABASE_URL: process.env.REACT_APP_SUPABASE_URL,
    REACT_APP_SUPABASE_ANON_KEY: process.env.REACT_APP_SUPABASE_ANON_KEY,
    REACT_APP_SOCKET_URL: process.env.REACT_APP_SOCKET_URL,
    REACT_APP_SOCKET_SERVER: process.env.REACT_APP_SOCKET_SERVER,
    REACT_APP_TURN_SERVERS: process.env.REACT_APP_TURN_SERVERS,
    REACT_APP_API_SERVER: process.env.REACT_APP_API_SERVER,
    REACT_APP_TUTOR_API_URL: process.env.REACT_APP_TUTOR_API_URL,
    REACT_APP_MEETINGS_API_URL: process.env.REACT_APP_MEETINGS_API_URL,
    REACT_APP_USE_MOCK_DATA: process.env.REACT_APP_USE_MOCK_DATA,
    // Quiz Worker Configuration
    REACT_APP_QUIZ_WORKER_URL: process.env.REACT_APP_QUIZ_WORKER_URL,
    REACT_APP_QUIZ_PROJECT_URL: process.env.REACT_APP_QUIZ_PROJECT_URL,
    REACT_APP_REFERENCES_R2_WORKER_URL: process.env.REACT_APP_REFERENCES_R2_WORKER_URL,
    // R2 Configuration
    R2_BUSINESS_BUCKET_NAME: process.env.R2_BUSINESS_BUCKET_NAME,
    R2_USER_BUCKET_NAME: process.env.R2_USER_BUCKET_NAME,
    R2_PUBLIC_URL: process.env.R2_PUBLIC_URL,
    // Image CDN Configuration
    REACT_APP_IMAGE_CDN_URL: process.env.REACT_APP_IMAGE_CDN_URL,
    // Version and Error Reporting
    REACT_APP_VERSION: process.env.REACT_APP_VERSION,
    REACT_APP_ERROR_REPORTING_URL: process.env.REACT_APP_ERROR_REPORTING_URL,
    REACT_APP_ERROR_REPORTING_KEY: process.env.REACT_APP_ERROR_REPORTING_KEY,
    REACT_APP_ERROR_SECRET_KEY: process.env.REACT_APP_ERROR_SECRET_KEY,
    // Logging Configuration
    REACT_APP_LOG_ENDPOINT: process.env.REACT_APP_LOG_ENDPOINT,
    PUBLIC_URL: process.env.PUBLIC_URL,
    // Render-specific environment variables
    RENDER_EXTERNAL_URL: process.env.RENDER_EXTERNAL_URL,
    RENDER_EXTERNAL_HOSTNAME: process.env.RENDER_EXTERNAL_HOSTNAME,
};
// Validate environment variables
try {
    const definedEnv = Object.entries(clientEnv).reduce((acc, [key, value]) => {
        if (value !== undefined) {
            acc[key] = value;
        }
        return acc;
    }, {});
    // Set default values for development/testing
    if (clientEnv.NODE_ENV === 'development' || clientEnv.NODE_ENV === 'test') {
        clientEnv.REACT_APP_API_URL = clientEnv.REACT_APP_API_URL;
        clientEnv.REACT_APP_SUPABASE_URL = clientEnv.REACT_APP_SUPABASE_URL;
        clientEnv.REACT_APP_SUPABASE_ANON_KEY = clientEnv.REACT_APP_SUPABASE_ANON_KEY;
        clientEnv.REACT_APP_PUBLIC_STRIPE_PUBLISHABLE_KEY = clientEnv.REACT_APP_PUBLIC_STRIPE_PUBLISHABLE_KEY;
    }
    // Ensure we have at least one API URL
    // Only throw in production - in dev/test, allow fallback
    if (!clientEnv.REACT_APP_API_URL && clientEnv.NODE_ENV === 'production') {
        throw new Error('REACT_APP_API_URL must be set in Cloudflare Pages environment variables. ' +
            'Go to Cloudflare Dashboard → Pages → Settings → Environment Variables and add REACT_APP_API_URL');
    }
    // Only validate in production
    if (clientEnv.NODE_ENV === 'production') {
        clientSchema.parse(definedEnv);
    }
    // Optional: Consolidate similar variables
    clientEnv.REACT_APP_API_SERVER = clientEnv.REACT_APP_API_SERVER ?? clientEnv.REACT_APP_API_URL;
    clientEnv.REACT_APP_SOCKET_SERVER = clientEnv.REACT_APP_SOCKET_SERVER ?? clientEnv.REACT_APP_SOCKET_URL;
}
catch (error) {
    // Only log detailed errors in development mode
    if (clientEnv.NODE_ENV !== 'production') {
        if (error instanceof z.ZodError) {
            console.error('❌ Environment validation failed:');
            console.error(JSON.stringify(error.errors, null, 2));
            console.error('\n📝 Missing or invalid environment variables:');
            error.errors.forEach(err => {
                const path = err.path.join('.');
                console.error(`   - ${path}: ${err.message}`);
            });
            console.error('\n💡 Make sure root .env contains all required REACT_APP_* variables');
            console.error('   Required variables:');
            console.error('   - REACT_APP_SUPABASE_URL');
            console.error('   - REACT_APP_SUPABASE_ANON_KEY');
            console.error('   - REACT_APP_PUBLIC_STRIPE_PUBLISHABLE_KEY');
            console.error('   - REACT_APP_API_URL');
            console.error('   - REACT_APP_SOCKET_URL (or REACT_APP_SOCKET_SERVER)');
        }
        else {
            console.error('Unexpected error during environment validation:', error);
        }
        console.warn('⚠️  Environment validation failed in development/test mode, using defaults');
    }
    // Always throw error in production for security
    if (clientEnv.NODE_ENV === 'production') {
        const missingVars = [];
        if (!clientEnv.REACT_APP_SUPABASE_URL)
            missingVars.push('REACT_APP_SUPABASE_URL');
        if (!clientEnv.REACT_APP_SUPABASE_ANON_KEY)
            missingVars.push('REACT_APP_SUPABASE_ANON_KEY');
        if (!clientEnv.REACT_APP_PUBLIC_STRIPE_PUBLISHABLE_KEY)
            missingVars.push('REACT_APP_PUBLIC_STRIPE_PUBLISHABLE_KEY');
        if (!clientEnv.REACT_APP_API_URL)
            missingVars.push('REACT_APP_API_URL');
        if (!clientEnv.REACT_APP_SOCKET_URL && !clientEnv.REACT_APP_SOCKET_SERVER) {
            missingVars.push('REACT_APP_SOCKET_URL or REACT_APP_SOCKET_SERVER');
        }
        if (missingVars.length > 0) {
            throw new Error(`Invalid environment configuration. Missing required variables: ${missingVars.join(', ')}. ` +
                `Ensure these are set in Render environment variables or root .env at build time.`);
        }
    }
}
// Export validated environment
export default clientEnv;
